import React, { useState, useEffect } from 'react';
import {
    Grid,
    Typography,
    TextField,
    Button,
    DialogContent,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    FormHelperText,
} from '@mui/material';
import { Formik, Form, Field } from 'formik';
import DraggableDialog from '../../../../ui/draggableDialog';
import { toast } from 'react-toastify';
import { isLocalDev } from '../../../../../utils/environment';
import { ContactLensConfigurationService, ValidDataService } from '../../../../../service/validDataService';
import { initialValuesContactLensPricingForm } from './contactLensPricingInputForm';

// Helper function to format lens type for display
const formatLensType = (lensType) => {
    if (!lensType) return '';
    return lensType.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

export default function EditContactLensPricingForm({ open, onClose, onRecordEdit, recordToEdit, isCopy = false }) {
    const [manufacturers, setManufacturers] = useState([]);
    const [contactLensModels, setContactLensModels] = useState([]);
    const [schedules, setSchedules] = useState([]);

    useEffect(() => {
        if (open) {
            // Fetch contact lens manufacturers
            ValidDataService.getManufacturersByType('contact_lens').then(result => {
                const { code, data } = result;
                if (code === 0) {
                    setManufacturers(data || []);
                }
            }).catch(error => {
                if (isLocalDev) console.error(error);
            });

            // Fetch schedules
            ContactLensConfigurationService.getActiveSchedules().then(result => {
                if (result?.code === 0) {
                    setSchedules(result.data || []);
                }
            }).catch(console.error);

            // Fetch contact lens models if manufacturer is set
            if (recordToEdit?.contactLensModel?.manufacturer?.id) {
                ContactLensConfigurationService.getModelsWithLimit(0, 1000, [], []).then(result => {
                    const { code, data } = result;
                    if (code === 0) {
                        const filteredModels = data?.rows?.filter(
                            m => m.manufacturerId === recordToEdit.contactLensModel.manufacturer.id && m.status === 'active'
                        ) || [];
                        setContactLensModels(filteredModels);
                    }
                }).catch(error => {
                    if (isLocalDev) console.error(error);
                });
            }
        }
    }, [open, recordToEdit]);

    const handleManufacturerChange = async (manufacturerId, setFieldValue) => {
        setFieldValue('manufacturerId', manufacturerId);
        setFieldValue('contactLensModelId', '');
        setContactLensModels([]);

        if (manufacturerId) {
            try {
                const result = await ContactLensConfigurationService.getModelsWithLimit(0, 1000, [], []);
                const { code, data } = result;
                if (code === 0) {
                    const filteredModels = data?.rows?.filter(
                        m => m.manufacturerId === manufacturerId && m.status === 'active'
                    ) || [];
                    setContactLensModels(filteredModels);
                }
            } catch (error) {
                if (isLocalDev) console.error(error);
            }
        }
    };

    const handleSubmit = async (values, { resetForm }) => {
        try {
            // Convert empty maxBoxQuantity to null
            const dataToSubmit = {
                ...values,
                maxBoxQuantity: values.maxBoxQuantity === '' ? null : values.maxBoxQuantity,
            };

            let response;

            if (isCopy) {
                const { id, ...newValues } = dataToSubmit;
                response = await ContactLensConfigurationService.addPricingMatrix(newValues);
            } else {
                response = await ContactLensConfigurationService.updatePricingMatrix(dataToSubmit);
            }
            
            const { code, data, message } = response;
            if (code === 0) {
                toast.success(message);
                onClose();
                onRecordEdit(data);
            } else {
                toast.error(message);
            }
        } catch (error) {
            if (isLocalDev) {
                console.log(error);
            }
            //toast.error('Failed to update pricing entry');
        }
    };

    // Prepare initial values
    const getInitialValues = () => {
        if (!recordToEdit) return initialValuesContactLensPricingForm;

        return {
            ...initialValuesContactLensPricingForm,
            ...recordToEdit,
            manufacturerId: recordToEdit.contactLensModel?.manufacturer?.id || '',
            maxBoxQuantity: recordToEdit.maxBoxQuantity || '',
        };
    };

    return (
        <DraggableDialog title={isCopy ? "Copy Pricing Entry" : "Edit Pricing Entry"} onClose={onClose} open={open} width={'md'}>
            <DialogContent sx={{ mt: 1, mb: 1 }}>
                <Formik
                    initialValues={getInitialValues()}
                    onSubmit={handleSubmit}
                    enableReinitialize
                >
                    {({ isSubmitting, values, setFieldValue, handleChange, handleBlur }) => (
                        <Form>
                            <Grid container spacing={2}>
                                {/* Manufacturer Selection - Used to filter models */}
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Filter by Manufacturer</InputLabel>
                                        <Select
                                            name="manufacturerId"
                                            value={values.manufacturerId || ''}
                                            onChange={(e) => handleManufacturerChange(e.target.value, setFieldValue)}
                                            onBlur={handleBlur}
                                            label="Filter by Manufacturer"
                                        >
                                            {manufacturers.map((mfr) => (
                                                <MenuItem key={mfr.id} value={mfr.id}>
                                                    {mfr.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <FormHelperText>Select to filter contact lens models</FormHelperText>
                                    </FormControl>
                                </Grid>

                                {/* Contact Lens Model */}
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth size="small" required disabled={!values.manufacturerId}>
                                        <InputLabel>Contact Lens Model</InputLabel>
                                        <Select
                                            name="contactLensModelId"
                                            value={values.contactLensModelId || ''}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            label="Contact Lens Model"
                                        >
                                            {contactLensModels.map((model) => (
                                                <MenuItem key={model.id} value={model.id}>
                                                    {model.modelName}
                                                    {model.lensType && ` - ${formatLensType(model.lensType)}`}
                                                    {model.material?.name && ` (${model.material.name})`}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Schedule */}
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth size="small" required>
                                        <InputLabel>Wear Schedule</InputLabel>
                                        <Select
                                            name="scheduleId"
                                            value={values.scheduleId || ''}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            label="Wear Schedule"
                                        >
                                            {schedules.map((schedule) => (
                                                <MenuItem key={schedule.id} value={schedule.id}>
                                                    {schedule.name} ({schedule.daysSupply} days)
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Lenses Per Box */}
                                <Grid item xs={12} sm={6}>
                                    <Field
                                        as={TextField}
                                        name="lensesPerBox"
                                        label="Lenses Per Box"
                                        type="number"
                                        required
                                        fullWidth
                                        size="small"
                                        inputProps={{ min: 1 }}
                                    />
                                </Grid>

                                {/* Box Quantity Tier */}
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                                        Box Quantity Tier (for volume pricing)
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Field
                                        as={TextField}
                                        name="minBoxQuantity"
                                        label="Minimum Box Quantity"
                                        type="number"
                                        required
                                        fullWidth
                                        size="small"
                                        inputProps={{ min: 1 }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Field
                                        as={TextField}
                                        name="maxBoxQuantity"
                                        label="Maximum Box Quantity"
                                        type="number"
                                        fullWidth
                                        size="small"
                                        inputProps={{ min: 1 }}
                                        helperText="Leave blank for unlimited"
                                    />
                                </Grid>

                                {/* Pricing */}
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                                        Pricing (per box)
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Field
                                        as={TextField}
                                        name="baseCost"
                                        label="Base Cost"
                                        type="number"
                                        required
                                        fullWidth
                                        size="small"
                                        inputProps={{ step: "0.01", min: 0 }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Field
                                        as={TextField}
                                        name="baseRetailPrice"
                                        label="Retail Price"
                                        type="number"
                                        required
                                        fullWidth
                                        size="small"
                                        inputProps={{ step: "0.01", min: 0 }}
                                    />
                                </Grid>

                                {/* Status */}
                                <Grid item xs={12} sm={6}>
                                    <Field
                                        as={TextField}
                                        name='status'
                                        label="Status"
                                        fullWidth
                                        select
                                        size="small"
                                    >
                                        <MenuItem value={"active"}>Active</MenuItem>
                                        <MenuItem value={"inactive"}>Inactive</MenuItem>
                                    </Field>
                                </Grid>

                                {/* Notes */}
                                <Grid item xs={12}>
                                    <Field
                                        as={TextField}
                                        name="notes"
                                        label="Notes"
                                        multiline
                                        rows={2}
                                        fullWidth
                                        size="small"
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        disabled={isSubmitting}
                                    >
                                        {isCopy ? 'Copy' : 'Update'}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Form>
                    )}
                </Formik>
            </DialogContent>
        </DraggableDialog>
    );
}
